import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import dayjs from 'dayjs';
import { CalendarEvent, Listing } from '../../schemas/calendar';
import { getColorByType } from '../../utils/StatusColors';

interface WeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  listings: Listing[];
  onEventClick: (event: CalendarEvent) => void;
  onSlotClick: (start: Date, end: Date, listingId?: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  listings,
  onEventClick,
  onSlotClick
}) => {
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);
  const weekStart = dayjs(currentDate).startOf('week');
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
  const TIME_COL_PX = 72;
  const MIN_DAY_WIDTH = 120;
  const gridTemplateColumns = `${TIME_COL_PX}px repeat(7, minmax(${MIN_DAY_WIDTH}px, 1fr))`;
  const gridMinWidth = TIME_COL_PX + 7 * MIN_DAY_WIDTH;

  const getEventsForDayAndHour = (day: dayjs.Dayjs, hour: number) => {
    return events.filter(event => {
      const eventStart = dayjs(event.startTime);
      const eventEnd = dayjs(event.endTime);
      return (
        eventStart.isBefore(day.endOf('day')) &&
        eventEnd.isAfter(day.startOf('day'))
      );
    });
  };

  const getEventHeight = (event: CalendarEvent) => {
    const start = dayjs(event.startTime);
    const end = dayjs(event.endTime);
    const durationInHours = end.diff(start, 'minute') / 60;
    return Math.max(durationInHours * 60, 30);
  };

  const getEventColor = (event: CalendarEvent) => {
    const listing = listings.find(l => l.id === event.listingId);
    switch (event.status) {
      case 'confirmed':
        return listing?.color || '#7c3aed';
      case 'pending':
        return '#f59e0b';
      case 'cancelled':
        return '#6b7280';
      default:
        return listing?.color || '#7c3aed';
    }
  };

  const isToday = (day: dayjs.Dayjs) => day.isSame(dayjs(), 'day');

  return (
    <Box sx={{ height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Box sx={{ minWidth: gridMinWidth }}>
      {/* Header with Days */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          borderBottom: '2px solid',
          borderColor: 'grey.200',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.default',
          zIndex: 10
        }}
      >
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            borderRight: '1px solid',
            borderColor: 'grey.200',
            position: 'sticky',
            left: 0,
            zIndex: 11,
            backgroundColor: 'background.default',
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="primary"
            sx={{ fontSize: { xs: '0.75rem', md: '1.25rem' } }}
          >
            Time
          </Typography>
        </Box>
        {weekDays.map((day) => (
          <Box
            key={day.format('YYYY-MM-DD')}
            sx={{
              p: { xs: 1, md: 2 },
              borderRight: '1px solid',
              borderColor: 'grey.200',
              textAlign: 'center',
              backgroundColor: isToday(day) ? 'primary.light' : 'transparent',
              color: isToday(day) ? 'white' : 'inherit'
            }}
          >
            <Typography 
              variant="subtitle1" 
              fontWeight={600} 
              color={isToday(day) ? 'inherit' : 'primary'}
              sx={{ fontSize: { xs: '0.8rem', md: '1.25rem' } }}
            >
              {day.format('ddd')}
            </Typography>
            <Typography 
              variant="h6" 
              fontWeight={700}
              color={isToday(day) ? 'inherit' : 'text.secondary'}
              sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }}
            >
              {day.format('DD')}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Time Grid */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'stretch',
            position: 'relative'
          }}
        >
          {/* Time Labels */}
          <Box sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
            {timeSlots.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 60,
                  boxSizing: 'border-box',
                  borderBottom: '1px solid',
                  borderColor: 'grey.100',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  p: 0.5
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {dayjs().hour(hour).format('h A')}
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Day Columns */}
          {weekDays.map((day) => {
            const dayEvents = events.filter(event => dayjs(event.startTime).isSame(day, 'day'));
            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  position: 'relative',
                  borderRight: '1px solid',
                  borderColor: 'grey.200',
                  backgroundColor: isToday(day) ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isToday(day) 
                      ? 'rgba(124, 58, 237, 0.1)' 
                      : 'grey.50'
                  }
                }}
                onClick={(e) => {
                  // Optionally handle slot click for the whole day
                }}
              >
                {timeSlots.map((hour) => (
                  <Box
                    key={`${day.format('YYYY-MM-DD')}-${hour}`}
                    sx={{
                      height: 60,
                      boxSizing: 'border-box',
                      borderBottom: '1px solid',
                      borderColor: 'grey.100',
                    }}
                  />
                ))}
                {/* Render events absolutely positioned */}
                {dayEvents.map((event, index) => {
                  const eventStart = dayjs(event.startTime);
                  const eventEnd = dayjs(event.endTime);
                  const top = eventStart.hour() * 60 + eventStart.minute();
                  const height = Math.max(eventEnd.diff(eventStart, 'minute'), 30);
                  const listing = listings.find(l => l.id === event.listingId);
                  return (
                    <Paper
                      key={event.id}
                      elevation={2}
                      sx={{
                        position: 'absolute',
                        top,
                        left: 4 + (index * 2),
                        right: 4,
                        height,
                        // backgroundColor: getEventColor(event),
                        backgroundColor: getColorByType(listing!),
                        p: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        zIndex: 1,
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 8px 30px rgba(124, 58, 237, 0.3)'
                        },
                        transition: 'all 0.3s ease',
                        overflow: 'hidden'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {event.title}
                      </Typography>
                      {event.customerName && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            opacity: 0.8,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.7rem'
                          }}
                        >
                          {event.customerName}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          opacity: 0.8,
                          fontSize: '0.6rem'
                        }}
                      >
                        {eventStart.format('h:mm A')} - {eventEnd.format('h:mm A')}
                      </Typography>
                      {event.status !== 'confirmed' && (
                        <Chip
                          label={event.status}
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '0.6rem',
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            mt: 0.5
                          }}
                        />
                      )}
                    </Paper>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default WeekView;